import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material"
import React, { ChangeEvent, useCallback } from "react"
import { customPalette } from "../../../../theme"
import { SelectStrOption } from "./SelectStrOption"
import { eventFromValue } from "../../../core/services/helper-service"

interface IProps<F> {
  fieldName: {
    [K in Extract<keyof F, string>]: F[K] extends string | null ? K : never
  }[Extract<keyof F, string>]
  label: string
  form: F
  options: SelectStrOption[]
  errors: Record<string, string | undefined>
  handleChange(event: ChangeEvent<HTMLInputElement>): void
}

export function SelectInputStringNoneOption<F>({
  fieldName,
  label,
  form,
  errors,
  options,
  handleChange,
}: Readonly<IProps<F>>): React.JSX.Element {
  const internalHandleChange: (event: SelectChangeEvent) => void = useCallback(
    (event) => {
      const newSelectedValue: string = event.target.value
      if (newSelectedValue !== "") {
        handleChange(eventFromValue(fieldName, newSelectedValue))
      } else {
        handleChange(eventFromValue(fieldName, null))
      }
    },
    [fieldName, handleChange]
  )

  const selectValue: string = form[fieldName] === null ? "" : (form[fieldName] as string)

  return (
    <FormControl
      fullWidth
      error={!!errors[fieldName]}
      margin="normal"
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 4,
          overflow: "hidden",
          backgroundColor: customPalette.lightWhite,
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
            borderRadius: 4,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
            borderRadius: 4,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
            borderRadius: 4,
          },
        },
      }}>
      <InputLabel sx={{ fontSize: { lg: 14, xl: 16 } }}>{label}</InputLabel>
      <Select
        id={fieldName}
        aria-describedby={fieldName}
        labelId={`${label}-id-label`}
        value={selectValue}
        onChange={internalHandleChange}
        label={label}
        error={!!errors[fieldName]}
        sx={{ fontSize: { lg: 14, xl: 16 } }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: 4,
              elevation: 2, // Réduit l'élévation
              boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
            },
          },
        }}>
        <MenuItem value="">
          <em>Aucun</em>
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.label} value={option.value} sx={{ fontSize: { lg: 14, xl: 16 } }}>
            {option.label}
          </MenuItem>
        ))}
      </Select>

      <FormHelperText error id={fieldName}>
        {errors[fieldName]}
      </FormHelperText>
    </FormControl>
  )
}
